import React, { useState } from 'react';
import { CheckCircle2, XCircle, Search, RefreshCw } from 'lucide-react';

const runSeoCheck = (formData) => {
  const seoTitle = formData.seo_title || formData.title || '';
  const metaDesc = formData.seo_description || '';
  const focusKeyword = (formData.focus_keyword || '').trim();
  const kw = focusKeyword.toLowerCase();
  const firstParaMatch = (formData.content || '').match(/<p[^>]*>(.*?)<\/p>/i);
  const firstPara = firstParaMatch ? firstParaMatch[1].replace(/<[^>]+>/g, '').toLowerCase() : '';

  return [
    {
      label: 'Title Tag length (30-60 characters recommended)',
      passed: seoTitle.length >= 30 && seoTitle.length <= 60,
      detail: `${seoTitle.length} characters`,
    },
    {
      label: 'Meta Description length (120-155 characters recommended)',
      passed: metaDesc.length >= 120 && metaDesc.length <= 155,
      detail: `${metaDesc.length} characters`,
    },
    {
      label: 'Featured Image Alt Text present',
      passed: !!(formData.featured_image_alt || '').trim(),
    },
    {
      label: 'URL slug is valid (lowercase letters, numbers, hyphens only)',
      passed: /^[a-z0-9]+(-[a-z0-9]+)*$/.test(formData.slug || ''),
    },
    {
      label: 'At least one hashtag added',
      passed: (formData.hashtags || []).length > 0,
    },
    {
      label: 'Focus Keyword is set',
      passed: !!focusKeyword,
    },
    {
      label: 'Focus Keyword appears in Title, Meta Description, or first paragraph',
      passed: !!kw && (seoTitle.toLowerCase().includes(kw) || metaDesc.toLowerCase().includes(kw) || firstPara.includes(kw)),
    },
  ];
};

const BlogAssistantTab = ({ formData, setFormData }) => {
  const [results, setResults] = useState(null);

  const handleRunCheck = () => setResults(runSeoCheck(formData));

  const passedCount = results ? results.filter((r) => r.passed).length : 0;

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">Focus Keyword *</label>
        <input
          type="text"
          data-testid="assistant-focus-keyword-input"
          value={formData.focus_keyword || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, focus_keyword: e.target.value }))}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter the main keyword you want to rank for..."
        />
        <p className="mt-1 text-xs text-gray-500">Used to check keyword presence in your Title, Meta Description, and first paragraph</p>
      </div>

      <button
        type="button"
        data-testid="assistant-run-seo-check-btn"
        onClick={handleRunCheck}
        className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
      >
        <Search size={18} />Run SEO Check
      </button>

      {results && (
        <div data-testid="assistant-seo-results" className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-800">SEO Checklist</h4>
            <span className="px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-700">
              {passedCount}/{results.length} passed
            </span>
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <RefreshCw size={12} />This is a non-blocking check — you can still publish with warnings.
          </p>
          {results.map((check, idx) => (
            <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${check.passed ? 'bg-green-50' : 'bg-red-50'}`}>
              {check.passed ? (
                <CheckCircle2 size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${check.passed ? 'text-green-800' : 'text-red-800'}`}>{check.label}</p>
                {check.detail && <p className="text-xs text-gray-500 mt-0.5">{check.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogAssistantTab;
